import React from 'react';
import styles from './css/index.module.css';
import { useDispatch, useSelector } from 'react-redux';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { UpdateProjectEmailPreferences } from '../../redux/usersSlice';

const DEFAULT_PROJECT_EMAIL_PREFERENCES = {
    daily_documents_email: false,
    task_comment_instant_email: false,
    task_completed_instant_email: false,
    daily_tasks_email: false,
    daily_cross_project_tasks_email: false,
    new_irl_req_instant_email: false,
    bulk_irl_req_instant_email: false,
    new_irl_res_instant_email: false,
    irl_comment_instant_email: false,
    daily_irls_email: false,
    weekly_summary_email: false,
};

const EmailPreferences = ({ compareObjectsValues }) => {
    const dispatch = useDispatch();
    const { selectedProject } = useSelector(state => state.projectData);
    const { user, emailPreferences, updateEmailPreferencesLoading } = useSelector(state => state.user);

    const [formData, setFormData] = React.useState({ ...DEFAULT_PROJECT_EMAIL_PREFERENCES });

    const handleChange = e => {
        const { name, checked } = e.target;
        setFormData({ ...formData, [name]: checked });
    };

    const handleSubmit = async () => {
        if (!selectedProject?.id) {
            console.error('No project selected');
            return;
        }

        // Get original preferences to compare
        const originalPreferences = getCurrentProjectPreferences();

        // Find only the fields that have changed
        const changedFields = {};
        Object.keys(formData).forEach(key => {
            if (formData[key] !== originalPreferences[key]) {
                changedFields[key] = formData[key];
            }
        });

        // Only send request if there are changes
        if (Object.keys(changedFields).length === 0) {
            console.log('No changes to save');
            return;
        }

        try {
            await dispatch(
                UpdateProjectEmailPreferences({
                    projectId: selectedProject.id,
                    preferences: changedFields,
                }),
            );
            console.log('Email preferences updated successfully. Changed fields:', Object.keys(changedFields));
        } catch (error) {
            console.error('Failed to update email preferences:', error);
        }
    };

    // Get current project's email preferences
    const getCurrentProjectPreferences = () => {
        if (!emailPreferences?.project_preferences || !selectedProject?.id) {
            return { ...DEFAULT_PROJECT_EMAIL_PREFERENCES };
        }

        const currentProjectPrefs = emailPreferences.project_preferences.find(project => project.project_id === selectedProject.id);

        return currentProjectPrefs?.preferences || { ...DEFAULT_PROJECT_EMAIL_PREFERENCES };
    };

    const handleSetFormValues = () => {
        const projectPreferences = getCurrentProjectPreferences();
        setFormData({
            daily_documents_email: projectPreferences.daily_documents_email ?? false,
            task_comment_instant_email: projectPreferences.task_comment_instant_email ?? false,
            task_completed_instant_email: projectPreferences.task_completed_instant_email ?? false,
            daily_tasks_email: projectPreferences.daily_tasks_email ?? false,
            daily_cross_project_tasks_email: projectPreferences.daily_cross_project_tasks_email ?? false,
            new_irl_req_instant_email: projectPreferences.new_irl_req_instant_email ?? false,
            bulk_irl_req_instant_email: projectPreferences.bulk_irl_req_instant_email ?? false,
            new_irl_res_instant_email: projectPreferences.new_irl_res_instant_email ?? false,
            irl_comment_instant_email: projectPreferences.irl_comment_instant_email ?? false,
            daily_irls_email: projectPreferences.daily_irls_email ?? false,
            weekly_summary_email: projectPreferences.weekly_summary_email ?? false,
        });
    };

    React.useEffect(() => {
        if (selectedProject && emailPreferences) {
            handleSetFormValues();
        }
    }, [selectedProject, emailPreferences]);

    return (
        <div style={{ height: '90vh' }} className={styles.componentCard}>
            <h5 className={styles.componentTitle}>Email Preferences</h5>

            <div className="mb-4 ms-1">
                <p className={styles.fieldLabel + ' mb-4'}>Choose which email updates you'd like to receive. You can update these settings anytime.</p>

                {/* Documents Section */}
                <div className="mb-4">
                    <h6 className={styles.fieldLabel + ' mb-3'} style={{ fontWeight: 'bold', color: '#333' }}>
                        Documents
                    </h6>
                    <div className="mb-4 ms-3">
                        <div className="d-flex align-items-center">
                            <input
                                type="checkbox"
                                id="daily_documents_email"
                                name="daily_documents_email"
                                checked={formData.daily_documents_email}
                                onChange={handleChange}
                                className="me-2"
                                style={{ transform: 'scale(1.2)' }}
                            />
                            <label htmlFor="daily_documents_email" className={styles.fieldLabel + ' mb-0'}>
                                Daily Documents Email
                            </label>
                        </div>
                        <div className="ms-4">
                            <small className="text-muted">Sent every weekday at 7:00 AM GMT with document updates.</small>
                        </div>
                    </div>
                </div>

                {/* Tasks Section */}
                <div className="mb-4">
                    <h6 className={styles.fieldLabel + ' mb-3'} style={{ fontWeight: 'bold', color: '#333' }}>
                        Tasks
                    </h6>
                    <div className="ms-3">
                        {/* Daily Tasks Email */}
                        <div className="mb-4">
                            <div className="d-flex align-items-center">
                                <input
                                    type="checkbox"
                                    id="daily_tasks_email"
                                    name="daily_tasks_email"
                                    checked={formData.daily_tasks_email}
                                    onChange={handleChange}
                                    className="me-2"
                                    style={{ transform: 'scale(1.2)' }}
                                />
                                <label htmlFor="daily_tasks_email" className={styles.fieldLabel + ' mb-0'}>
                                    Daily Tasks Email
                                </label>
                            </div>
                            <div className="ms-4">
                                <small className="text-muted">Sent every weekday at 7:00 AM GMT with task updates.</small>
                            </div>
                        </div>

                        {/* Task Comment Instant Email */}
                        <div className="mb-4">
                            <div className="d-flex align-items-center">
                                <input
                                    type="checkbox"
                                    id="task_comment_instant_email"
                                    name="task_comment_instant_email"
                                    checked={formData.task_comment_instant_email}
                                    onChange={handleChange}
                                    className="me-2"
                                    style={{ transform: 'scale(1.2)' }}
                                />
                                <label htmlFor="task_comment_instant_email" className={styles.fieldLabel + ' mb-0'}>
                                    Task Comment Notifications
                                </label>
                            </div>
                            <div className="ms-4">
                                <small className="text-muted">Sent immediately when someone comments on your tasks.</small>
                            </div>
                        </div>

                        {/* Task Completed Instant Email */}
                        <div className="mb-4">
                            <div className="d-flex align-items-center">
                                <input
                                    type="checkbox"
                                    id="task_completed_instant_email"
                                    name="task_completed_instant_email"
                                    checked={formData.task_completed_instant_email}
                                    onChange={handleChange}
                                    className="me-2"
                                    style={{ transform: 'scale(1.2)' }}
                                />
                                <label htmlFor="task_completed_instant_email" className={styles.fieldLabel + ' mb-0'}>
                                    Task Completion Notifications
                                </label>
                            </div>
                            <div className="ms-4">
                                <small className="text-muted">Sent immediately when tasks are completed.</small>
                            </div>
                        </div>

                        {/* Daily Cross-Project Tasks Email */}
                        <div className="mb-4">
                            <div className="d-flex align-items-center">
                                <input
                                    type="checkbox"
                                    id="daily_cross_project_tasks_email"
                                    name="daily_cross_project_tasks_email"
                                    checked={formData.daily_cross_project_tasks_email}
                                    onChange={handleChange}
                                    className="me-2"
                                    style={{ transform: 'scale(1.2)' }}
                                />
                                <label htmlFor="daily_cross_project_tasks_email" className={styles.fieldLabel + ' mb-0'}>
                                    Daily Cross-Project Tasks Email
                                </label>
                            </div>
                            <div className="ms-4">
                                <small className="text-muted">Sent daily with all your assigned tasks across all projects.</small>
                            </div>
                        </div>
                    </div>
                </div>

                {/* IRLs Section */}
                <div className="mb-4">
                    <h6 className={styles.fieldLabel + ' mb-3'} style={{ fontWeight: 'bold', color: '#333' }}>
                        IRLs
                    </h6>
                    <div className="ms-3">
                        {/* Daily IRLs Email */}
                        <div className="mb-4">
                            <div className="d-flex align-items-center">
                                <input
                                    type="checkbox"
                                    id="daily_irls_email"
                                    name="daily_irls_email"
                                    checked={formData.daily_irls_email}
                                    onChange={handleChange}
                                    className="me-2"
                                    style={{ transform: 'scale(1.2)' }}
                                />
                                <label htmlFor="daily_irls_email" className={styles.fieldLabel + ' mb-0'}>
                                    Daily IRLs Email
                                </label>
                            </div>
                            <div className="ms-4">
                                <small className="text-muted">Sent every weekday at 7:00 AM GMT with IRL updates.</small>
                            </div>
                        </div>

                        {/* New IRL Request Instant Email */}
                        <div className="mb-4">
                            <div className="d-flex align-items-center">
                                <input
                                    type="checkbox"
                                    id="new_irl_req_instant_email"
                                    name="new_irl_req_instant_email"
                                    checked={formData.new_irl_req_instant_email}
                                    onChange={handleChange}
                                    className="me-2"
                                    style={{ transform: 'scale(1.2)' }}
                                />
                                <label htmlFor="new_irl_req_instant_email" className={styles.fieldLabel + ' mb-0'}>
                                    New IRL Request Notifications
                                </label>
                            </div>
                            <div className="ms-4">
                                <small className="text-muted">Sent immediately when new IRL requests are made.</small>
                            </div>
                        </div>

                        {/* Bulk IRL Request Instant Email */}
                        <div className="mb-4">
                            <div className="d-flex align-items-center">
                                <input
                                    type="checkbox"
                                    id="bulk_irl_req_instant_email"
                                    name="bulk_irl_req_instant_email"
                                    checked={formData.bulk_irl_req_instant_email}
                                    onChange={handleChange}
                                    className="me-2"
                                    style={{ transform: 'scale(1.2)' }}
                                />
                                <label htmlFor="bulk_irl_req_instant_email" className={styles.fieldLabel + ' mb-0'}>
                                    Bulk IRL Request Notifications
                                </label>
                            </div>
                            <div className="ms-4">
                                <small className="text-muted">Sent immediately when bulk IRL requests are made.</small>
                            </div>
                        </div>

                        {/* New IRL Response Instant Email */}
                        <div className="mb-4">
                            <div className="d-flex align-items-center">
                                <input
                                    type="checkbox"
                                    id="new_irl_res_instant_email"
                                    name="new_irl_res_instant_email"
                                    checked={formData.new_irl_res_instant_email}
                                    onChange={handleChange}
                                    className="me-2"
                                    style={{ transform: 'scale(1.2)' }}
                                />
                                <label htmlFor="new_irl_res_instant_email" className={styles.fieldLabel + ' mb-0'}>
                                    IRL Response Notifications
                                </label>
                            </div>
                            <div className="ms-4">
                                <small className="text-muted">Sent immediately when IRL responses are submitted.</small>
                            </div>
                        </div>

                        {/* IRL Comment Instant Email */}
                        <div className="mb-4">
                            <div className="d-flex align-items-center">
                                <input
                                    type="checkbox"
                                    id="irl_comment_instant_email"
                                    name="irl_comment_instant_email"
                                    checked={formData.irl_comment_instant_email}
                                    onChange={handleChange}
                                    className="me-2"
                                    style={{ transform: 'scale(1.2)' }}
                                />
                                <label htmlFor="irl_comment_instant_email" className={styles.fieldLabel + ' mb-0'}>
                                    IRL Comment Notifications
                                </label>
                            </div>
                            <div className="ms-4">
                                <small className="text-muted">Sent immediately when someone comments on IRLs.</small>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Weekly Summary Section */}
                <div className="mb-4">
                    <h6 className={styles.fieldLabel + ' mb-3'} style={{ fontWeight: 'bold', color: '#333' }}>
                        Weekly Summary
                    </h6>
                    <div className="ms-3">
                        <div className="mb-4">
                            <div className="d-flex align-items-center">
                                <input
                                    type="checkbox"
                                    id="weekly_summary_email"
                                    name="weekly_summary_email"
                                    checked={formData.weekly_summary_email}
                                    onChange={handleChange}
                                    className="me-2"
                                    style={{ transform: 'scale(1.2)' }}
                                />
                                <label htmlFor="weekly_summary_email" className={styles.fieldLabel + ' mb-0'}>
                                    Weekly Summary Email
                                </label>
                            </div>
                            <div className="ms-4">
                                <small className="text-muted">Sent every Friday at 7:00 AM GMT with weekly project summary.</small>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Submit button section */}
            <div className={styles.buttonDiv}>
                {!compareObjectsValues(getCurrentProjectPreferences(), formData) && (
                    <div className="d-flex align-items-center justify-content-between">
                        <button onClick={handleSubmit} className={styles.submitButton}>
                            Save {updateEmailPreferencesLoading && <AiOutlineLoading3Quarters className="ms-2 rotateLoader" />}
                        </button>
                        <span onClick={handleSetFormValues} className={styles.linkBtn}>
                            Revert
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EmailPreferences;
